/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { APIEnum } from '../../../../loadbalancer/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { AdcModel, ShuttleModel } from '../../../shared/models';
import {
    AdcsApiService,
    BackupApiService,
    NamespacesApiService,
    SecurityApiService,
    ShuttleApiService
} from '../../../shared/http';
import { AdcsUtilsService, NamespaceService, PortalRbacService } from '../../../shared/services/utils';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
    ConnectionDetailsService,
    DrawerService,
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    SocketService,
    UserService,
    UtilsService
} from '../../../../../core/services';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { VendorEnum } from '../../../shared/enums';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-adcs-list',
    templateUrl: './adcs.list.component.html',
    styleUrls: ['./adcs.list.component.scss']
})

export class AdcsListComponent extends BaseComponent implements OnInit {
    /**
     * Reference to the content of this component.
     */
    @ViewChild('actionDots', { static: true }) public actionDots: TemplateRef<any>;

    /**
     * Reference to the content of this component.
     */
    @ViewChild('drawerContent', { static: true }) public drawerContent: TemplateRef<any>;

    /**
     * Content for the template to be passed to the drawer service.
     */
    public templateContent: AdcModel;

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string | [] = '';

    /**
     * Search terms from table component.
     */
    public searchedValues: any[] = [];

    /**
     * Dropdown content object.
     */
    public dropdown: any = {
        namespaces: []
    };

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Max limit reached
     */
    public maxLimitReached: boolean = false;

    /**
     * Store the drawer open state for showing and hidden buttons in the view
     */
    public drawerOpen: boolean = false;

    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    /**
     * Store the security details for each ADC for use in the drawer.
     */
    public storedSecurityDetails: any = [];

    /**
     * Stored ADCs for socket service.
     */
    public storedAdcs: any[] = [];

    /**
     * Stored shuttles for reference.
     */
    public storedShuttles: any[] = [];

    /**
     * Stored subscription tier.
     */
    public tier: string;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Stored backups for reference.
     * @private
     */
    private storedBackups: any[] = [];

    /**
     * Store organisation id.
     * @private
     */
    private readonly organisationId: string;

    constructor(
        public adcUtilsService: AdcsUtilsService,
        private adcApiService: AdcsApiService,
        private backupApiService: BackupApiService,
        private cdr: ChangeDetectorRef,
        private connectionDetailsService: ConnectionDetailsService,
        private drawerService: DrawerService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceApiService: NamespacesApiService,
        private namespaceService: NamespaceService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private portalRbacService: PortalRbacService,
        private socketService: SocketService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private securityApiService: SecurityApiService,
        private shuttleApiService: ShuttleApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.organisationId = this.userService.user.organisationId;
        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        this.tier = this.portalRbacService.selectedTier?.tier;

        this.setupSubscribers();
        this.getNamespaces();
        this.setupSocketListeners();
        this.registerButtons();
    }

    ngOnInit(): void {
        this.setUpPageData();
    }

    /**
     * Max limit of ADCs reached, disable 'Add' button.
     */
    public maxLimit($event: any): void {
        // TODO when RBAC is completed there should be a buttons variable.
        this.maxLimitReached = !$event;
    }

    /**
     * Show alternative content helper.
     */
    public showAlternativeContent(): boolean {
        return !this.tableRows.length;
    }

    /**
     * Check if row is part of a cluster
     * @param row
     */
    // TODO review this
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public isInCluster(row: AdcModel): boolean {
        return false;

        // return row.peerIds.length > 1;
    }

    /**
     * Show matched tags in the table.
     * @param row
     */
    public getMatchTags(row: AdcModel): string[] {
        return row.tags?.filter((item: string) => this.searchedValues.includes(item));
    }

    /**
     * Click handler for ADC.
     * @param row
     */
    public openDrawer(row: AdcModel): void {
        if ((row && typeof row === 'object' && Object.keys(row).length) && row !== this.templateContent) {
            this.setDrawerContent(row);
        } else {
            this.drawerService.close();
            const emptyData: any = setTimeout((): void => {
                this.templateContent = null;
                clearInterval(emptyData);
            }, 500);
            this.drawerOpen = false;
        }
    }

    /**
     * Store the searched values.
     * @param $event
     */
    public setSearchValues($event: any): void {
        this.searchedValues = $event;
    }

    /**
     * Notify the user why the ADC is waiting for adoption.
     */
    public notifyUserOfPendingShuttle(): void {
        const confirmationObject = {
            title: 'adc_pending_adoption',
            message: 'adc_pending_adoption_message',
            callBack: () => {
                this.router.navigateByUrl('/cm/adcs/shuttle').then();
                this.modalService.close('confirmation-box');
            },
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Check ADC is ready.
     * @param adoptionStatus
     */
    public adcIsReady(adoptionStatus: string): boolean {
        return adoptionStatus !== 'pending' && adoptionStatus !== 'adopting' && adoptionStatus !== 'errorConnectionTimeout';
    }

    /**
     * Get locked state icon.
     * @param locked
     */
    public getLockedStateIcon(locked: any): string {
        return locked ? 'locked' : 'unlocked';
    }

    /**
     * Get image src based on running status.
     * @param row
     */
    public getRunningStatusSrc(row: AdcModel): string {
        let statusName: string;
        if (row.adoptionStatus !== 'errorConnectionTimeout') {
            switch (row.status) {
                case 'unknown':
                case 'offline':
                    statusName = 'disconnected';
                    break;
                case 'pending':
                    statusName = 'pending';
                    break;
                default:
                    statusName = 'connected';
            }
        } else {
            statusName = 'pending';
        }

        return `./assets/centralisedManagement/icons/running-status-${statusName}.svg`;
    }

    /**
     * Set colour for ADC status.
     * @param row
     */
    public getAdcStatusClassName(row: AdcModel): string {
        if (row.status === 'pending' || row.adoptionStatus === 'errorConnectionTimeout') {
            return 'pending';
        }

        return row.status;
    }

    /**
     * Get HA Status class based the state of the ADC.
     * @param row
     */
    public getHaStatusClassName(row: AdcModel): string {
        if ((row.vendor === VendorEnum.GENERIC || row.vendor === VendorEnum.LBENTERPRISE)) {
            if (row.vendor === VendorEnum.GENERIC) {
                return 'not-applicable';
            }

            if (row.status === 'offline') {
                return 'offline';
            }

            if (!row.haStatus) {
                return 'unavailable';
            }

            if (row.haStatus === 'passive') {
                return 'passive';
            }

            if (row.haStatus === 'active') {
                return 'active';
            }
        }

        return 'unavailable';
    }

    /**
     * Show there is an update available.
     * @param row
     */
    public showUpdateIndicator(row: any): boolean {
        const isIncludedVendor: boolean = [VendorEnum.LBENTERPRISE, VendorEnum.LBENDURANCE].includes(row.vendor);

        return row.updateStatus === 'available' && isIncludedVendor;
    }

    /**
     * Disable the update now button if the adc is not online or there are no updates available.
     * @param row
     */
    public disabledUpdateButtons(row: AdcModel): boolean {
        const isIncludedVendor: boolean = [VendorEnum.LBENTERPRISE, VendorEnum.LBENDURANCE].includes(row.vendor);

        return !this.permissionsLookup['adcs-list-system-update'] ||
            row.status !== 'online' ||
            !this.adcUtilsService.versionCanUpdate(row) ||
            row.updateStatus !== 'available' || !isIncludedVendor;
    }

    /**
     * Update ADC.
     * @param row
     */
    public systemUpdateAdc(row: AdcModel): void {
        if (!this.disabledUpdateButtons(row)) {
            this.adcApiService.systemUpdate(row.namespaceId, row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        const timerId: any = setTimeout((): void => {
                            row.status = 'updating';
                            this.loadingService.loadingActionDotsEmitter.next(false);
                            clearTimeout(timerId);
                        }, 2000);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsListComponent: systemUpdateAdc', 'Error submitting data', error);
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        } else {
            const timerId: any = setTimeout((): void => {
                this.notifyService.warning('adc_this_cant_message', { messageTranslate: true });
                this.loadingService.loadingActionDotsEmitter.next(false);
                clearTimeout(timerId);
            }, 1500);
        }
    }

    /**
     * Disable the backup now button if the adc is not online or there are no updates available.
     * @param row
     * @param viewOnly
     */

    public disabledBackupButtons(row: AdcModel, viewOnly?: boolean): boolean {
        const isIncludedVendor: boolean = [
            VendorEnum.LBENTERPRISE,
            VendorEnum.LBENDURANCE,
            VendorEnum.KEMP,
            VendorEnum.F5,
            VendorEnum.CITRIX
        ].includes(row.vendor);

        return !this.permissionsLookup['adcs-list-backup'] ||
            row.status !== 'online' ||
            !isIncludedVendor ||
            (this.backupLimitReached(row) && !viewOnly);
    }

    /**
     * Backup limit reached.
     * @param row
     */
    public backupLimitReached(row: AdcModel): boolean {
        const backupLimit: number = this.portalRbacService.selectedTier?.backups;

        return backupLimit ? row.backupCount >= backupLimit : false;
    }

    /**
     * Backup ADC now.
     * @param row
     */
    public backupAdc(row: AdcModel): void {
        if (!this.disabledBackupButtons(row)) {
            row.status = 'backup';
            const apiCalls = {
                backupAction: this.backupApiService.backupAdc(row.namespaceId, row.id),
                backups: this.backupApiService.getBackupsByNamespaceId(this.selectedNamespaceKey)
            };

            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        const backupCount: any = data['backups'].filter((item: any): boolean => item.adcID === row.id).length;
                        row.backupCount = backupCount + 1;
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsListComponent: backupAdc', 'Unable to complete backup.', error);
                    }
                });
        } else {
            const timerId: any = setTimeout((): void => {
                this.notifyService.warning('adc_this_cant_message', { messageTranslate: true });
                this.loadingService.loadingActionDotsEmitter.next(false);
                clearTimeout(timerId);
            }, 1500);
        }
    }

    /**
     * Disable the schedule buttons if the adc is not online or there are no updates available.
     * @param row
     */
    public disabledScheduleButtons(row: AdcModel): boolean {
        const isIncludedVendor: boolean = [
            VendorEnum.LBENTERPRISE,
            VendorEnum.LBENDURANCE,
            VendorEnum.KEMP,
            VendorEnum.F5,
            VendorEnum.CITRIX
        ].includes(row.vendor);

        return !this.permissionsLookup['adcs-list-add-schedule'] || row.status !== 'online' || !isIncludedVendor;
    }

    /**
     * Disable the schedule buttons if the adc is not online or there are no updates available.
     * @param row
     */
    public disabledScheduleViewButtons(row: AdcModel): boolean {
        const isIncludedVendor: boolean = [
            VendorEnum.LBENTERPRISE,
            VendorEnum.LBENDURANCE,
            VendorEnum.KEMP,
            VendorEnum.F5,
            VendorEnum.CITRIX
        ].includes(row.vendor);

        return !this.permissionsLookup['adcs-list-view-schedule'] || !isIncludedVendor;
    }

    /**
     * Confirm remove adc.
     * @param row
     */
    public confirmRemoveADC(row: AdcModel): void {
        const confirmationObject = {
            title: 'adc_remove',
            message: 'adc_remove_message',
            callBack: this.deleteAdc.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Get button text for either adopting or timed out
     * @param row
     */
    public getButtonText(row: AdcModel): string {
        return row.adoptionStatus === 'errorConnectionTimeout' ? 'timed_out' : 'adopting';
    }

    /**
     * Delete an ADC.
     * @param row
     */
    public deleteAdc(row: AdcModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.adcApiService.delete(row.namespaceId, row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        if (row.id === this.templateContent?.id) {
                            this.drawerService.close();
                        }
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsListComponent: deleteAdc', 'Error submitting data', error);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Get simple status.
     * @param adc
     */
    public isWarpButtonEnabled(adc: AdcModel): boolean {
        const isIncludedVendor: boolean = this.isIncludedVendor(adc);

        return (adc.adoptionStatus === 'adopted'
            && adc.warpEnabled
            && isIncludedVendor
            && ['backup', 'updating', 'online'].includes(adc.status)
        );
    }

    /**
     * Is included vendor.
     * @param adc
     */
    public isIncludedVendor(adc: AdcModel): boolean {
        return [
            VendorEnum.LBENTERPRISE,
            VendorEnum.LBENDURANCE,
            VendorEnum.KEMP,
            VendorEnum.F5,
            VendorEnum.CITRIX
        ].includes(adc.vendor);
    }

    /**
     * Set navigation message in drawer.
     * @param statusId
     */
    public getNavigationMessage(statusId: number): string {
        const message = {
            1: 'adc_generic_message',
            2: 'adc_no-warp_message',
            3: 'adc_offline_message',
            4: ''
        };

        return statusId <= 4 ? message[statusId] : 'n/a';
    }

    /**
     * Navigate based on the row status.
     * @param adc
     * @param canWarp
     */
    public navigateBasedOnRowStatus(adc: AdcModel, canWarp: boolean): Observable<any> {
        return new Observable((observe: Subscriber<any>): void => {
            if (canWarp) {
                const token = this.userService.getUser('JWT');
                const apiAddress: string = this.connectionDetailsService.getAddress();
                const apiPort: number = this.connectionDetailsService.getPort();
                const warpAddress: string = `warp/https://${adc.ipAddress}:${adc.port}/?token=${token}`;
                const url: string =
                    `https://${apiAddress}:${apiPort}/api/${APIEnum.POST_VERSION}/${adc.namespaceId}/adcs/${adc.id}/${warpAddress}`;
                window.open(url);
                observe.next();
                observe.complete();
            } else {
                this.logService.warn('ADC not warp enabled', 'ADCs must be warp enabled to link to them in this way');
                observe.next();
                observe.complete();
            }
        });
    }

    /**
     * Navigate to local address.
     * @param adc
     */
    public navigateToDirectAdc(adc: AdcModel): void {
        const protocol: 'http' | 'https' = adc.port.toString() === '80' ? 'http' : 'https';
        const url: string = `${protocol}://${adc.ipAddress}:${adc.port}/`;

        this.loadingService.loadingActionDotsEmitter.next(false);
        this.loadingService.loadingButtonEmitter.next(false);

        window.open(url);
    }

    /**
     * Get security styles.
     * @param risk
     */
    public getSecurityStyle(risk: string): string {
        switch (risk.toLowerCase()) {
            case 'critical':
            case 'high':
                return 'filter: invert(13%) sepia(97%) saturate(5393%) hue-rotate(317deg) brightness(109%) contrast(107%)';
            case 'medium':
                return 'filter: invert(12%) sepia(81%) saturate(6800%) hue-rotate(282deg) brightness(109%) contrast(122%)';
            case 'low':
                return 'filter: invert(34%) sepia(57%) saturate(2650%) hue-rotate(226deg) brightness(90%) contrast(82%)';
            default:
                return undefined;
        }
    }

    /**
     * Set class based on severity level.
     * @param severityScore
     */
    public getSeverityScoreClass(severityScore: number): string {
        return this.adcUtilsService.getSeverityScoreClass(severityScore);
    }

    /**
     * Confirm delete namespace.
     */
    public confirmDeleteNamespace(): void {
        const confirmationObject = {
            title: 'namespace_delete',
            message: 'namespace_delete_message',
            callBack: this.deleteNamespace.bind(this, this.selectedNamespaceKey),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdcsListComponent: setupSubscribers', 'Unable to get selected namespace.', error);
                }
            });
    }

    /**
     * Get all the user-friendly namespace names.
     * @private
     */
    private getNamespaces(): void {
        this.dropdown.namespaces = this.namespaceService.namespaces;
    }

    /**
     * Get the most upto date list of ADCs.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            adcs: this.adcApiService.getAll(this.selectedNamespaceKey),
            shuttles: this.shuttleApiService.getShuttleList(this.selectedNamespaceKey),
            backups: this.backupApiService.getBackupsByNamespaceId(this.selectedNamespaceKey)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    this.storedAdcs = dataGroup['adcs'];
                    this.storedShuttles = this.filterAdoptedShuttles(dataGroup['shuttles']);
                    this.storedBackups = dataGroup['backups'];
                    this.populateTableRows(dataGroup['adcs']);
                    this.checkForSearchQuery();
                    this.adcUtilsService.checkLocalStorageForDrawerContent(this.tableRows, this.setDrawerContent.bind(this));
                    this.isLoading = false;
                },
                error: (error: ErrorResponseModel): void => {
                    this.isLoading = false;
                    this.logService.error('AdcsListComponent: getAdcList', 'Unable to get ADC list', error);
                }
            });
    }

    /**
     * Filter adopted shuttles.
     * @param shuttles
     * @private
     */
    private filterAdoptedShuttles(shuttles: ShuttleModel[]): ShuttleModel[] {
        return shuttles.filter(item => item.adoptionStatus === 'adopted');
    }

    /**
     * Populate table rows.
     * @param adcs
     * @private
     */
    private populateTableRows(adcs: AdcModel[]): void {
        this.storedAdcs = adcs;
        this.tableRows = [];
        if (adcs?.length) {
            const namespaces: any[] = this.namespaceService.namespaces;

            for (const adc of adcs) {
                const shuttle: ShuttleModel = this.storedShuttles.find(item => item.id === adc.sidecarID);
                if (shuttle?.vendor >= 0) {
                    shuttle.vendorIcon = this.adcUtilsService.getVendorIconName(shuttle.vendor);
                    shuttle.vendorName = this.adcUtilsService.getVendorTitle(shuttle.vendor);
                    const namespace = (shuttle.namespaceID || shuttle.namespaceId || 0);
                    shuttle.namespace = this.adcUtilsService.getNamespaceFriendlyName(
                        namespaces, namespace
                    );
                }

                const backupCount = this.storedBackups.filter(item => item.adcID === adc.id).length;

                // TODO removed until other issues with PeerIds are sorted.
                // let peerIds: any[] = adc.peerIDs || [];
                // if (peerIds?.length) {
                //     if (peerIds.every(item => adcs.find(adc => adc.id === item).status !== 3)) {
                //         // Add this ADC's id to the list for grouping.
                //         peerIds.push(adc.id);
                //     } else {
                //         // Empty the peerIds array if the ADCs aren't online
                //         peerIds = [];
                //     }
                // }
                const vendor = adc.vendor;

                this.tableRows.push({
                    adoptionStatus: adc.adoptionStatus,
                    applianceId: adc.applianceID || adc.applianceId,
                    backupCount,
                    haStatus: adc.haStatus,
                    haStatusText: this.getHaStatusClassName(adc),
                    id: adc.id,
                    ipAddress: adc.ipAddress,
                    label: adc.label,
                    lastHandShakeTime: adc.lastHandShakeTime,
                    locked: adc.locked,
                    namespaceId: adc.namespaceID || adc.namespaceId,
                    namespace: this.adcUtilsService.getNamespaceFriendlyName(namespaces, (adc.namespaceID || adc.namespaceId)),
                    notes: adc.notes,
                    orgId: adc.orgID || adc.orgId,
                    // peerIds: this.utilsService.sortArray(peerIds), // required for matching data
                    port: adc.port,
                    productName: this.adcUtilsService.getProductNameFromVendor(vendor),
                    tags: adc.tags,
                    serial: adc.serial?.length ? adc.serial : 'n/a',
                    shuttle,
                    status: adc.status,
                    statusRunning: adc.status,
                    updateStatus: adc.updateStatus,
                    updateVersion: adc.updateVersion,
                    vendor: vendor,
                    vendorIcon: this.adcUtilsService.getVendorIconName(vendor),
                    vendorName: this.adcUtilsService.getVendorTitle(vendor),
                    version: this.adcUtilsService.formatVersionNumber(adc.version),
                    vulnerable: adc.vulnerable,
                    vulnerableSeverity: adc.vulnerableSeverity.toLowerCase(),
                    warpEnabled: adc.warpEnabled
                });
            }
        }

        // this.tableRows = [...this.groupADCsByClusters(this.tableRows)];
    }

    /**
     * Group ADCs by Cluster
     * @param rows
     * @private
     */
    private groupADCsByClusters(rows: AdcModel[]): any[] {
        const clusteredNodes = [];
        const singleNodes = [];
        for (const row of rows) {
            if (row.peerIds.length > 1) {
                clusteredNodes.push(row);
            } else {
                singleNodes.push(row);
            }
        }

        let sortedClusteredNodes: any[] = [];
        let clusterIds: any[] = [];
        for (const node of clusteredNodes) {
            if (!clusterIds.includes(node.id)) {
                const matches: any = clusteredNodes.filter((item: any) => item.peerIds.includes(node.id));
                clusterIds = clusterIds.concat(matches.map((item: any) => item.id));
                const sorted: any = this.utilsService.sortArrayByProperty(matches, 'haStatus', ['active', 'passive']);
                sortedClusteredNodes = sortedClusteredNodes.concat(sorted);
            }
        }

        return sortedClusteredNodes.concat(singleNodes);
    }

    /**
     * Check for search query.
     * @private
     */
    private checkForSearchQuery(): void {
        const query: any = this.utilsService.getObjectValue(this.route, 'snapshot.queryParamMap.params.sFilter');
        if (query) {
            this.searchTerm = query;
            this.navigationService.removeParamsFromUrl();
            this.cdr.detectChanges();
        }
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        this.socketService.on('AdcAdd')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.storedAdcs.push(response.data);
                    this.populateTableRows(this.storedAdcs);
                }
            });

        this.socketService.on('AdcRemove')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    const removeIndex: number = this.storedAdcs.findIndex(item => item.id === response.data.id);
                    if (removeIndex !== -1) {
                        this.storedAdcs.splice(removeIndex, 1);
                        this.populateTableRows(this.storedAdcs);
                    }
                }
            });

        this.socketService.on('ADCStateChange')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (this.utilsService.getObjectValue(response, 'data.id')) {
                        this.updatePageOnADCStatusChange(response);
                    }
                }
            });
    }

    /**
     * Update the page on ADC status change.
     * @param response
     * @private
     */
    private updatePageOnADCStatusChange(response: { data: AdcModel }): void {
        const adcIndex: number = this.storedAdcs.findIndex(item => item.id === response.data.id);
        const propertiesToIgnore = ['lastHandShakeTime', 'peerIDs'];
        if (adcIndex !== -1 && this.adcUtilsService.dataHasChanged(this.storedAdcs[adcIndex], response.data, propertiesToIgnore)) {
            this.storedAdcs[adcIndex] = response.data;
            const clonedTableRows = this.utilsService.duplicateArray(this.storedAdcs);
            clonedTableRows[adcIndex] = response.data;
            this.populateTableRows(clonedTableRows);

            const newContent = this.adcUtilsService.updateTemplateContent(this.templateContent, response, this.tableRows);
            if (newContent) {
                this.templateContent = newContent;
            }
        }
    }

    /**
     * Set drawer content.
     * @param row
     * @private
     */
    private setDrawerContent(row?: any): void {
        if (this.isIncludedVendor(row)) {
            this.getSecurityDetailsForDrawer(row.id);
        } else {
            row['vulnerableSupported'] = false;
        }

        this.templateContent = row;
        this.drawerService.open({
            title: row.label,
            actionDots: (row.adoptionStatus === 'adopted' || row.vendor === 0) ? this.actionDots : null,
            content: null,
            template: this.drawerContent,
            data: row
        });
        this.drawerOpen = true;
    }

    /**
     * Get security details for drawer by ADC id.
     * @param id
     * @private
     */
    private getSecurityDetailsForDrawer(id: string): void {
        this.securityApiService.getSecurityInsightByAdc(this.selectedNamespaceKey, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: (data: any): void => {
                        if (data?.length && !this.storedSecurityDetails.includes(id)) {
                            this.storedSecurityDetails[id] = this.sortedSecurityDetails(data);
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('SecurityDetailsComponent:getSecurityDetailsForDrawer', 'Unable to get page data. ', error);
                    }
                }
            );
    }

    /**
     * Sort the security details by their severity.
     * @param data
     * @private
     */
    private sortedSecurityDetails(data: any[]): any[] {
        return this.utilsService.sortArrayByProperty(data, 'baseScore', 'desc');
    }

    /**
     * Delete a name from the namespaces store.
     * @param id
     * @private
     */
    private deleteNamespace(id: string): Observable<any> {
        return new Observable((observer) => {
            this.namespaceApiService.delete(this.organisationId, id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.updateNamespacesList(this.organisationId);
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AdcsListComponent: deleteNamespace', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the name space service and dropdown.
     * @param organisationId
     * @private
     */
    private updateNamespacesList(organisationId: string): void {
        this.namespaceApiService.getAll(organisationId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.namespaceService.namespaces = data;
                    this.namespaceService.selectedNamespace = <DropdownModel>{ key: 0, value: 'All' };
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdcsListComponent: updateNamespacesList', 'Unable to update namespaces.', error);
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'adcs-list-add': [{ path: '/api/v1/:namespace/adcs/', methods: ['GET', 'POST'] }],
            'adcs-list-edit-adc': [
                { path: '/api/v1/:namespace/sidecars/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/:id', methods: ['PATCH'] }
            ],
            'adcs-list-delete-adc': [{ path: '/api/v1/:namespace/adcs/:id', methods: ['DELETE'] }],
            'adcs-list-view-schedule': [
                { path: '/api/v1/:namespace/scheduler/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] }
            ],
            'adcs-list-add-schedule': [
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] },
                { path: '/api/v1/:namespace/scheduler/', methods: ['POST'] }
            ],
            'adcs-list-view-backups': [
                { path: '/api/v1/:namespace/backups/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] }
            ],
            'adcs-list-backup': [{ path: '/api/v1/:namespace/adcs/:adcID/backup', methods: ['POST'] }],
            'adcs-list-system-update': [{ path: '/api/v1/:namespace/adcs/:id/updates', methods: ['PUT'] }],
            'adcs-list-view-insights': [{ path: '/api/v1/:namespace/security/insights/:adcid', methods: ['GET'] }],
            'namespace-list-delete': [{ path: '/api/v1/organisations/:orgid/namespaces/:id', methods: ['DELETE'] }],
            'adcs-list-warp': [{ path: '/api/v1/:namespace/adcs/:id/warp/:loc', methods: ['GET'] }],
            'adcs-list-direct': [{ path: '/api/v1/:namespace/adcs/:id/warp/:loc', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
